<template>
	<div>
		<Modal
			:is-open="isOpen"
			title="Hard Delete Now"
			@close="closeModal"
		>
			<template #body>
				<div class="hard-delete-content">
					<div class="message error">
						This permanently purges the account, its content, files and Stripe customer
						<strong>immediately</strong>. It cannot be undone and no email is sent to the user.
					</div>
					<p>Account: <strong>{{ user.email }}</strong></p>
					<label for="confirm-email">Type the account email to confirm:</label>
					<input
						id="confirm-email"
						v-model="typedEmail"
						type="text"
						autocomplete="off"
						:disabled="processing"
					>
					<div v-if="errorMessage" class="message error">
						{{ errorMessage }}
					</div>
					<div v-else-if="successMessage" class="message success">
						{{ successMessage }}
					</div>
				</div>
			</template>

			<template #actions>
				<button @click="closeModal" :disabled="processing">
					Cancel
				</button>
				<button class="danger" @click="hardDelete" :disabled="!canConfirm">
					{{ actionLabel }}
				</button>
			</template>
		</Modal>
	</div>
</template>

<script>
import Modal from './Modal.vue';

export default {
	name: 'hard-delete-now-modal',
	components: {
		Modal
	},
	store: ['users'],
	props: {
		isOpen: {
			type: Boolean,
			default: false
		},
		userId: {
			type: Number
		}
	},
	data() {
		return {
			typedEmail: '',
			errorMessage: null,
			successMessage: null,
			processing: false
		}
	},
	computed: {
		user() {
			return this.users[this.userId] || {
				user_uuid: null
			}
		},

		actionLabel() {
			if (this.successMessage) return 'Hard Delete Successful';
			return this.processing ? 'Deleting...' : 'Hard delete now';
		},

		// Only soft-deleted accounts can be purged, whatever opened the modal.
		canConfirm() {
			const email = this.user.email?.trim().toLowerCase();
			return !this.processing && !!this.user.deleted_date && !!email && this.typedEmail.trim().toLowerCase() === email;
		}
	},

	methods: {
		closeModal() {
			if (!this.processing) {
				this.$emit('close');
				this.resetForm();
			}
		},

		resetForm() {
			this.typedEmail = '';
			this.errorMessage = null;
			this.successMessage = null;
			this.processing = false;
		},

		hardDelete() {
			if (!this.canConfirm) return;

			this.errorMessage = null;
			this.successMessage = null;
			this.processing = true;

			this.$http.post(`/users/${this.user.user_uuid}/hardDelete`, { now: true })
				.then(({ data }) => {
					if (data.success) {
						this.successMessage = `Account has been permanently deleted (delete id ${data.deleteId}).`;

						// processing stays true until here so the guard in closeModal can't swallow this close
						setTimeout(() => {
							this.$emit('close');
							this.resetForm();
							this.$emit('purged');
						}, 2000);
					} else {
						this.processing = false;
						this.errorMessage = `The purge was queued but did not complete. Check the API logs (delete id ${data.deleteId}).`;
					}
				})
				.catch((error) => {
					this.processing = false;
					this.errorMessage = error.response?.data?.detail || error.message || 'An error occurred during hard delete';
					console.error('Hard delete now failed:', error);
				});
		}
	}
}
</script>

<style scoped>
	.hard-delete-content {
		padding: 20px 0;
	}

	.hard-delete-content input {
		width: 100%;
		box-sizing: border-box;
		margin: 8px 0 16px;
	}

	.message {
		padding: 12px;
		border-radius: 4px;
		margin-bottom: 12px;
	}

	.message.success {
		background: #d4edda;
		color: #155724;
		border: 1px solid #c3e6cb;
	}

	.message.error {
		background: #f8d7da;
		color: #721c24;
		border: 1px solid #f5c6cb;
	}

	.danger:not(:disabled) {
		background: #dc3545;
		color: #fff;
	}
</style>
